import * as enabledApi from '@shared/api/enabledPlayApi';
import { createUUID } from '@shared/constants/Keys';
import { DefaultVoiceLocale, locales, VOICE_LOCALE_FLAGS } from '@shared/constants/localization/LocalizationConstants';
import { useEditDeviceTypeStore } from '@shared/hooks/state/EditDeviceTypeContainer';
import { CustomControlModel } from '@shared/models/devices/CustomControlModel';
import { CustomControlsModel } from '@shared/models/devices/CustomControlsModel';
import { VoiceLocales } from '@shared/models/localization/Locales';
import { UpdateDeviceTypeCustomControlsRequest } from '@shared/models/devices/UpdateDeviceTypeCustomControlsRequest';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const FALLBACK_LOCALE: VoiceLocales = DefaultVoiceLocale;

export type PerLocaleField = 'name' | 'description' | 'defaultCommands';
export type SharedField = Exclude<keyof CustomControlModel, PerLocaleField | 'id'>;

const arraysEqual = (a: string[] | undefined, b: string[] | undefined): boolean => {
    const left = a ?? [];
    const right = b ?? [];
    if (left.length !== right.length) return false;
    return left.every((value, index) => value === right[index]);
};

const isControlCustomizedAgainst = (control: CustomControlModel, fallback: CustomControlModel | undefined): boolean => {
    if (!fallback) return false;
    return (
        control.name !== fallback.name ||
        (control.description ?? '') !== (fallback.description ?? '') ||
        !arraysEqual(control.defaultCommands, fallback.defaultCommands)
    );
};

export function useDeviceTypeCustomControlsEditor(organizationId: string | undefined, deviceId: string | undefined) {
    const { t } = useTranslation();
    const editCustomControls = useEditDeviceTypeStore((state) => state.editCustomControls);
    const [activeLocale, setActiveLocale] = useState<VoiceLocales>(FALLBACK_LOCALE);
    const [isSaving, setIsSaving] = useState(false);
    const [addedControlIds, setAddedControlIds] = useState<Set<string>>(new Set());
    // Stable React keys for newly-added controls. Maps the control's current id (which can
    // change as the admin edits it) back to the immutable UUID assigned at creation time,
    // so the AccordionRow doesn't remount on every keystroke in the ID field.
    const [controlRowKeys, setControlRowKeys] = useState<Map<string, string>>(new Map());

    const LOCALES = useMemo(
        () =>
            locales.voice.map((code) => ({
                code,
                label: t(`common:localization.localesShort.${code}`),
                flag: VOICE_LOCALE_FLAGS[code],
            })),
        [t]
    );

    const currentControls = useMemo<CustomControlModel[]>(() => {
        if (!editCustomControls) return [];
        return editCustomControls.find((c) => c.locale === activeLocale)?.controls ?? [];
    }, [editCustomControls, activeLocale]);

    const isControlComplete = (control: CustomControlModel): boolean => {
        const hasName = !!control.name && control.name.trim().length > 0;
        const hasDefaultCommand = !!(
            control.defaultCommands && control.defaultCommands.some((c) => c && c.trim().length > 0)
        );
        return hasName && hasDefaultCommand;
    };

    const isLocaleCustomized = (localeCode: VoiceLocales): boolean => {
        if (localeCode === FALLBACK_LOCALE) return true;
        const all = useEditDeviceTypeStore.getState().editCustomControls;
        if (!all) return false;
        const entry = all.find((c) => c.locale === localeCode);
        const fallback = all.find((c) => c.locale === FALLBACK_LOCALE);
        if (!entry || !fallback) return false;
        return entry.controls.some((control) => {
            const fallbackControl = fallback.controls.find((c) => c.id === control.id);
            return isControlCustomizedAgainst(control, fallbackControl);
        });
    };

    const getLocaleCompletion = (localeCode: VoiceLocales): number => {
        const all = useEditDeviceTypeStore.getState().editCustomControls;
        if (!all) return 0;
        const entry = all.find((c) => c.locale === localeCode);
        if (!entry || entry.controls.length === 0) return localeCode === FALLBACK_LOCALE ? 100 : 0;

        if (localeCode === FALLBACK_LOCALE) {
            const completed = entry.controls.filter(isControlComplete).length;
            return Math.round((completed / entry.controls.length) * 100);
        }

        const fallback = all.find((c) => c.locale === FALLBACK_LOCALE);
        const customizedComplete = entry.controls.filter((control) => {
            const fallbackControl = fallback?.controls.find((c) => c.id === control.id);
            return isControlCustomizedAgainst(control, fallbackControl) && isControlComplete(control);
        }).length;
        return Math.round((customizedComplete / entry.controls.length) * 100);
    };

    const handlePerLocaleFieldChange = (controlId: string, fieldName: PerLocaleField, value: string | string[]) => {
        const store = useEditDeviceTypeStore.getState();
        if (!store.editCustomControls) return;
        const updated = store.editCustomControls.map((entry) => {
            if (entry.locale !== activeLocale) return entry;
            return {
                ...entry,
                controls: entry.controls.map((control) =>
                    control.id === controlId ? { ...control, [fieldName]: value } : control
                ),
            };
        });
        store.setEditCustomControls(updated);
        store.setCustomControlsHasChanges(true);
    };

    const handleSharedFieldChange = <K extends SharedField>(
        controlId: string,
        fieldName: K,
        value: CustomControlModel[K]
    ) => {
        const store = useEditDeviceTypeStore.getState();
        if (!store.editCustomControls) return;
        const updated = store.editCustomControls.map((entry) => ({
            ...entry,
            controls: entry.controls.map((control) =>
                control.id === controlId ? { ...control, [fieldName]: value } : control
            ),
        }));
        store.setEditCustomControls(updated);
        store.setCustomControlsHasChanges(true);
    };

    const handleIdChange = (oldId: string, newId: string) => {
        if (!addedControlIds.has(oldId)) return;
        if (newId === oldId) return;
        const store = useEditDeviceTypeStore.getState();
        if (!store.editCustomControls) return;
        // Reject duplicates against any other control id (including other new controls and
        // existing read-only ids). This also prevents an empty id when another row is already
        // empty — without this, two rows could collide on the same React row key.
        const conflicts = store.editCustomControls.some((entry) =>
            entry.controls.some((c) => c.id === newId && c.id !== oldId)
        );
        if (conflicts) return;
        const updated = store.editCustomControls.map((entry) => ({
            ...entry,
            controls: entry.controls.map((control) => (control.id === oldId ? { ...control, id: newId } : control)),
        }));
        store.setEditCustomControls(updated);
        store.setCustomControlsHasChanges(true);
        setAddedControlIds((prev) => {
            const next = new Set(prev);
            next.delete(oldId);
            next.add(newId);
            return next;
        });
        setControlRowKeys((prev) => {
            const next = new Map(prev);
            const rowKey = next.get(oldId);
            if (rowKey !== undefined) {
                next.delete(oldId);
                next.set(newId, rowKey);
            }
            return next;
        });
    };

    const handleAddControl = (): string => {
        const store = useEditDeviceTypeStore.getState();
        const newId = createUUID();
        const blankControl: CustomControlModel = {
            id: newId,
            name: '',
            description: '',
            iconUrl: '',
            isProfileControl: false,
            isPrefixVoiceCommand: false,
            allowWhenDictating: false,
            allowsHold: false,
            isDeprecated: false,
            defaultCommands: [''],
            requiresEmployeeAccess: false,
            requiresStudentAccess: false,
            requiresGenericLicense: false,
            requiresPlusAccess: false,
        };
        const existing = store.editCustomControls ?? [];
        const updated: CustomControlsModel[] =
            existing.length > 0
                ? existing.map((entry) => ({
                      ...entry,
                      controls: [...entry.controls, { ...blankControl }],
                  }))
                : locales.voice.map((locale) => ({
                      id: '',
                      createdDate: new Date(),
                      modifiedDate: new Date(),
                      locale,
                      deviceTypeId: deviceId,
                      controls: [{ ...blankControl }],
                  }));
        store.setEditCustomControls(updated);
        store.setCustomControlsHasChanges(true);
        setAddedControlIds((prev) => new Set(prev).add(newId));
        // The newly-created id doubles as its own row key. Subsequent id edits will move the
        // mapping forward while keeping the stable rowKey value, so the React key stays put.
        setControlRowKeys((prev) => new Map(prev).set(newId, newId));
        return newId;
    };

    const isControlNew = useCallback((controlId: string): boolean => addedControlIds.has(controlId), [addedControlIds]);

    const getControlRowKey = useCallback(
        (controlId: string): string => controlRowKeys.get(controlId) ?? controlId,
        [controlRowKeys]
    );

    const saveCustomControls = async (
        onSuccess: (message: string) => void,
        onError: (message: string) => void
    ): Promise<boolean> => {
        const store = useEditDeviceTypeStore.getState();
        const current = store.editCustomControls;
        if (!organizationId || !deviceId || !current) {
            onError(t('common:saveError'));
            return false;
        }

        const entriesToSave = current.filter(
            (entry) => entry.locale === FALLBACK_LOCALE || isLocaleCustomized(entry.locale as VoiceLocales)
        );

        setIsSaving(true);
        try {
            const responses = await Promise.all(
                entriesToSave.map((entry) => {
                    const request: UpdateDeviceTypeCustomControlsRequest = { controls: entry.controls };
                    return enabledApi.updateOrgDeviceTypeCustomControls(
                        organizationId,
                        deviceId,
                        entry.locale,
                        request
                    );
                })
            );

            const failed = responses.filter((r) => r.resultType !== 'Ok');
            if (failed.length > 0) {
                onError(t('common:saveError'));
                return false;
            }

            onSuccess(t('common:saved'));

            store.setCustomControlsHasChanges(false);
            setAddedControlIds(new Set());
            setControlRowKeys(new Map());
            await store.initializeEditCustomControls(organizationId, deviceId);
            return true;
        } catch (error) {
            console.error('Error saving device type custom controls:', error);
            onError(t('common:saveError'));
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const totalControlCount = useMemo(() => currentControls.length, [currentControls]);

    const isControlCompleteById = useCallback(
        (controlId: string): boolean => {
            const control = currentControls.find((c) => c.id === controlId);
            return control ? isControlComplete(control) : false;
        },
        [currentControls]
    );

    return {
        activeLocale,
        setActiveLocale,
        isSaving,
        LOCALES,
        currentControls,
        totalControlCount,
        getLocaleCompletion,
        isLocaleCustomized,
        isControlCompleteById,
        isControlNew,
        getControlRowKey,
        handlePerLocaleFieldChange,
        handleSharedFieldChange,
        handleIdChange,
        handleAddControl,
        saveCustomControls,
    };
}
